import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PhotoDrop } from "./PhotoDrop";
import { api, errMsg, CATS } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const UploadDialog = ({ open, onOpenChange, presetContest, onUploaded, onNeedCredits }) => {
  const { user, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("futebol");
  const [contestId, setContestId] = useState("none");
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle("");
    setContestId(presetContest?.id || "none");
    if (presetContest && presetContest.category !== "todas") setCategory(presetContest.category);
    api.get("/contests").then(({ data }) => setContests(data.filter((c) => c.status === "active")));
  }, [open, presetContest]);

  const available = contests.filter((c) => c.category === "todas" || c.category === category);
  const noCredits = user && user.photo_credits <= 0;

  const submit = async () => {
    if (!file) return toast.error("Escolha uma foto");
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("category", category);
    fd.append("contest_id", contestId === "none" ? "" : contestId);
    try {
      await api.post("/photos", fd);
      toast.success("Foto enviada! Ela aparecerá após a aprovação.");
      await refreshUser();
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto border-white/10 bg-[#0E0E15]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black uppercase">Enviar foto</DialogTitle>
          <DialogDescription data-testid="upload-credits-info">
            Você tem <b className="text-lime-300">{user?.photo_credits ?? 0}</b> crédito(s).{" "}
            {user?.free_credits > 0 ? `Inclui ${user.free_credits} foto grátis (não vale para concursos principais).` : "Cada foto usa 1 crédito."}
          </DialogDescription>
        </DialogHeader>
        {noCredits ? (
          <div className="rounded-xl border border-lime-400/30 bg-lime-400/5 p-5 text-center">
            <p className="font-display text-xl uppercase">Sem créditos de foto</p>
            <p className="mt-1 text-sm text-slate-400">Compre fotos extras para participar dos concursos principais.</p>
            <button data-testid="btn-buy-extra-photos" onClick={() => { onOpenChange(false); onNeedCredits?.(); }} className="btn-neon mt-4 rounded-full px-6 py-2.5 font-semibold">
              Comprar fotos extras
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <PhotoDrop file={file} onChange={setFile} testId="upload-dropzone" />
            <div className="space-y-1.5">
              <Label>Título</Label>
              <Input data-testid="upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Gol de bicicleta" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Modalidade</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setContestId("none"); }}>
                  <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Concurso (opcional)</Label>
                <Select value={contestId} onValueChange={setContestId}>
                  <SelectTrigger data-testid="upload-contest-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Apenas galeria</SelectItem>
                    {available.map((c) => <SelectItem key={c.id} value={c.id}>{c.is_main ? "★ " : ""}{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button data-testid="upload-submit-btn" disabled={loading} onClick={submit} className="btn-neon flex w-full items-center justify-center gap-2 rounded-full py-3 font-semibold disabled:opacity-50">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Enviar foto
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
